import {
  createAsyncThunk,
  nanoid,
  type Action,
  type ThunkAction,
} from '@reduxjs/toolkit';

import {
  deafDialClaimed,
  selectIsDeafDialInFlight,
  setDeafIsLoading,
  setDeafStatus,
} from 'features/call/call-deaf/store';
import type { RootState } from 'features/app/store/store';
import { InterpretingSessionType } from 'features/call/call-base/enums';
import { sendConnectionCallState } from 'features/call/call-base/store';
import type { DeafDisconnected } from 'features/call/call-deaf/interfaces';
import { deafDisconnectAnalytics } from 'features/call/call-status/helpers';
import { delay } from 'features/utils/helpers';

export const handleDeafDisconnectThunk = createAsyncThunk(
  'call-deaf/handleDeafDisconnect',
  async (
    {
      connectionChangeType,
      isConnectionCallStateNeeded = true,
    }: DeafDisconnected,
    { dispatch, getState }
  ) => {
    dispatch(setDeafIsLoading(true));
    dispatch(setDeafStatus('disconnected'));

    const state = getState() as RootState;
    const isAppUnloading = state.app.isUnloading;

    // if the app is unloading, do not send the deaf disconnect this is handled in the proxy.
    if (isAppUnloading) {
      return;
    }

    try {
      if (isConnectionCallStateNeeded) {
        await dispatch(
          sendConnectionCallState({
            connectionChangeType,
            interpretingSessionType: InterpretingSessionType.NORMAL,
          })
        ).unwrap();
      }

      dispatch(
        deafDisconnectAnalytics('handleDeafDisconnect', connectionChangeType)
      );
    } catch (error) {
      console.error('Error in handleDeafDisconnect:', error);
    } finally {
      // TODO this is necessary until we unify the disconnect/connect button as they are currently separate components
      await delay(4000);
      dispatch(setDeafIsLoading(false));
    }
  }
);

/**
 * Synchronously claims the right to set up an H2D dial. Returns the claim
 * token, or null when another dial is already in flight. The caller must
 * release the token with `deafDialReleased` once its dial attempt is over.
 */
export const claimDeafDial =
  (): ThunkAction<string | null, RootState, unknown, Action> =>
  (dispatch, getState) => {
    if (selectIsDeafDialInFlight(getState())) {
      return null;
    }

    const token = nanoid();
    dispatch(deafDialClaimed(token));

    return token;
  };
